"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronUp, CreditCard, ExternalLink, LogOut } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { ProductSwitcher } from "@/components/product-switcher";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { ESCAPE_LEVELS } from "@llmgateway/shared/sandbox-escape";
import { StudioNav } from "./studio-nav";
import type { Organization } from "@/lib/types";
interface EscapeSidebarProps {
  selectedOrganization: Organization | null;
  levelId: number;
  onSelectLevel: (levelId: number) => void;
  className?: string;
}
export function EscapeSidebar(t0) {
  const $ = _c(36);
  const {
    selectedOrganization,
    levelId,
    onSelectLevel,
    className
  } = t0;
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  let t1;
  if ($[0] !== queryClient || $[1] !== router || $[2] !== signOut) {
    t1 = async () => {
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            queryClient.clear();
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://lounge.llmgateway.io/login");
          }
        }
      });
    };
    $[0] = queryClient;
    $[1] = router;
    $[2] = signOut;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const logout = t1;
  const levelsHidden = sidebarState === "collapsed" && !isMobile;
  if (!isUserLoading && !user) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex w-full items-center gap-2"><ProductSwitcher /></div>;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    let t4;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="mb-2 font-medium">Sign in to play</div>;
      t4 = <p className="text-muted-foreground mb-3">The model spends your credits as it plays, so a signed-in account is required.</p>;
      $[5] = t3;
      $[6] = t4;
    } else {
      t3 = $[5];
      t4 = $[6];
    }
    let t5;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <SidebarHeader><div className="mb-4 flex flex-col items-center gap-4">{t2}<div className="w-full rounded-md border p-4 text-sm">{t3}{t4}<div className="flex items-center justify-end gap-2"><Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] !== className) {
      t6 = <Sidebar className={className}>{t5}</Sidebar>;
      $[8] = className;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  let t2;
  if ($[10] !== className) {
    t2 = cn(className, "max-md:hidden");
    $[10] = className;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SidebarHeader><SidebarMenu><SidebarMenuItem><ProductSwitcher /></SidebarMenuItem></SidebarMenu><StudioNav /></SidebarHeader>;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="text-muted-foreground px-4 py-2 text-xs font-medium tracking-wider uppercase">Levels</div>;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== levelId || $[16] !== onSelectLevel) {
    t6 = ESCAPE_LEVELS.map(level => <SidebarMenuItem key={level.id}><SidebarMenuButton isActive={level.id === levelId} onClick={() => onSelectLevel(level.id)} tooltip={level.name} className="h-auto py-2"><span className="text-muted-foreground font-mono text-xs">{String(level.id).padStart(2, "0")}</span><div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{level.name}</div><div className="text-muted-foreground truncate text-xs">{level.tagline}</div></div></SidebarMenuButton></SidebarMenuItem>);
    $[15] = levelId;
    $[16] = onSelectLevel;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t6) {
    t7 = <SidebarMenu className="px-2">{t6}</SidebarMenu>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== levelsHidden || $[21] !== t7) {
    t8 = <SidebarContent className="overflow-hidden pb-2">{t4}<div aria-hidden={levelsHidden} inert={levelsHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t5}{t7}</div></SidebarContent>;
    $[20] = levelsHidden;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SidebarLoungePoints />;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== selectedOrganization) {
    t10 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={selectedOrganization} isLoading={false} /></div>;
    $[24] = selectedOrganization;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== logout || $[27] !== user) {
    t11 = user ? <SidebarMenu><SidebarMenuItem><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={user.name ?? "User"} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"><div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg"><span className="text-xs font-semibold">{user.name?.split(" ").map(_temp).join("").toUpperCase().slice(0, 2) ?? "U"}</span></div><div className="grid flex-1 text-left text-sm leading-tight"><span className="truncate font-semibold">{user.name}</span><span className="text-muted-foreground truncate text-xs">{user.email}</span></div><ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" /></SidebarMenuButton></DropdownMenuTrigger><DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}><DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem><DropdownMenuSeparator /><DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem><DropdownMenuSeparator /><DropdownMenuItem className="justify-between gap-3" onSelect={_temp2}><span>Theme</span><div onClick={_temp3} onKeyDown={_temp4}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem><DropdownMenuSeparator /><DropdownMenuItem onClick={logout}><LogOut className="mr-2 h-4 w-4" />Log out</DropdownMenuItem></DropdownMenuContent></DropdownMenu></SidebarMenuItem></SidebarMenu> : null;
    $[26] = logout;
    $[27] = user;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <SidebarFooter>{t9}{t10}{t11}</SidebarFooter>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12 || $[33] !== t2 || $[34] !== t8) {
    t13 = <Sidebar collapsible="icon" className={t2}>{t3}{t8}{t12}</Sidebar>;
    $[32] = t12;
    $[33] = t2;
    $[34] = t8;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
}
function _temp4(event_1) {
  return event_1.stopPropagation();
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp2(event) {
  return event.preventDefault();
}
function _temp(n) {
  return n[0];
}